import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, XIcon, ArrowLeftIcon, PackageIcon, LogOutIcon, SettingsIcon, ChevronRightIcon, MessageSquareIcon, BanIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { ConsentCheckbox } from '../legal/ConsentCheckbox';
import { ShopConfig } from '../../data/shopConfig';
import { Order, CUSTOMER_STATUS, PAYMENT_METHOD_LABEL, PAYMENT_LABEL, methodOf, money } from '../../data/orders';

/** The signed-in shopper, as this shop sees them. */
export interface ShopSession {
  name: string;
  phone: string;
}

/* ------------------------------------------------------------------ control */

/**
 * The account slot in the shop header. Before sign-in it simply says "Sign in";
 * afterwards it becomes the shopper's own initial with a short menu. It stays
 * merchant-branded — this never looks like the Talab marketplace.
 */
export function ShopAccountControl({
  session,
  onSignIn,
  onMyOrders,
  onSignOut,
  dark
}: {
  session: ShopSession | null;
  onSignIn: () => void;
  onMyOrders: () => void;
  onSignOut: () => void;
  dark: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!session) return <button onClick={onSignIn} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-colors duration-150 ease-out" style={{
    border: '1px solid var(--m-hairline)',
    background: dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.6)',
    color: 'var(--m-on-bg)'
  }} data-id="element-4618" data-mp="26qu5uzz9hwm9">
        <UserIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-4619" data-mp="1nuc6bwe68sfg" />
        Sign in
      </button>;
  return <div className="relative" data-id="element-4620" data-mp="1ss49jmk9o0yy">
      <button onClick={() => setOpen(o => !o)} aria-label="Your account" className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold transition-colors duration-150 ease-out" style={{
      background: 'var(--mp)',
      color: 'var(--mp-t)'
    }} data-id="element-4621" data-mp="3i6sjlh5dx6tt">
        {session.name.charAt(0)}
      </button>

      {open && <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-id="element-4622" data-mp="3bqa76nh4ukub" />
          <motion.div initial={{
        opacity: 0,
        y: -6,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 top-12 z-50 w-60 bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden" data-id="element-4623" data-mp="3bsd7c0c01ucr">
            <div className="px-4 py-3.5 border-b border-slate-100" data-id="element-4624" data-mp="3pogyfkrktml0">
              <p className="text-sm font-bold text-dark-navy truncate" data-id="element-4625" data-mp="r1vz8j568mwy">
                {session.name}
              </p>
              <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-4626" data-mp="m7i148z4k1ol">
                {session.phone}
              </p>
            </div>
            <MenuItem icon={PackageIcon} label="My orders" onClick={() => {
          setOpen(false);
          onMyOrders();
        }} data-id="element-4627" data-mp="yjkbqg3w4qkz" />
            <MenuItem icon={SettingsIcon} label="Account" data-id="element-4628" data-mp="18ijeji1z5s02" />
            <MenuItem icon={LogOutIcon} label="Sign out" onClick={() => {
          setOpen(false);
          onSignOut();
        }} data-id="element-4629" data-mp="18fx8xnyn9mkh" />
          </motion.div>
        </>}
    </div>;
}
function MenuItem({
  icon: Icon,
  label,
  onClick
}: {
  icon: typeof UserIcon;
  label: string;
  onClick?: () => void;
}) {
  return <button onClick={onClick} className="w-full flex items-center gap-3 px-4 py-3 text-left text-[13px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-4630" data-mp="1rakmrb0n2um5">
      <Icon className="w-4 h-4 text-slate-400" strokeWidth={1.75} data-id="element-4631" data-mp="2xxiyenq61jj6" />
      {label}
    </button>;
}

/* ------------------------------------------------------------------ sign in */

/**
 * Phone and a code — Talab's existing global sign-in, wearing the merchant's
 * brand. An existing Talab account is simply recognised; a new one is created
 * silently along with this merchant's customer relationship.
 */
export function ShopSignInSheet({
  config,
  onClose,
  onSignedIn
}: {
  config: ShopConfig;
  onClose: () => void;
  onSignedIn: (session: ShopSession) => void;
}) {
  const [stage, setStage] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  // In the real product this is resolved server-side against the global
  // identity. Here, a known number is treated as a returning Talab account.
  const returning = phone.replace(/\D/g, '').endsWith('504419022');
  // A returning Talab account has already agreed; only new accounts are asked.
  const [agreed, setAgreed] = useState(false);
  const phoneValid = phone.replace(/\D/g, '').length >= 9;
  const needsConsent = phoneValid && !returning;
  return <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6" data-id="element-4632" data-mp="3syrp0a4cclxo">
      <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-4633" data-mp="1sgvcz1nungd" />
      <motion.div initial={{
      opacity: 0,
      y: 16,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden" data-id="element-4634" data-mp="phj8pup9hu52">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 p-2 rounded-lg text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-4635" data-mp="18gni54kptpx9">
          <XIcon className="w-4 h-4" data-id="element-4636" data-mp="1n2uhvzt3j6qz" />
        </button>

        <div className="px-7 pt-8 pb-7" data-id="element-4637" data-mp="1i49k071bgz5p">
          {config.logo ? <img src={config.logo} alt={config.publicName} className="h-8 w-auto object-contain" data-id="element-4638" data-mp="1d5g2m3iwso09" /> : <span className="font-display font-bold text-lg text-[var(--mp-deep)]" data-id="element-4639" data-mp="193l5zcbxayst">
              {config.publicName}
            </span>}

          {stage === 'phone' ? <>
              <h2 className="font-display font-bold text-xl text-dark-navy mt-5" data-id="element-4640" data-mp="zcqdhq8332yx">
                Sign in to {config.publicName}
              </h2>
              <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed" data-id="element-4641" data-mp="pgdmj03xb37v">
                Continue to view your orders and check out faster.
              </p>

              <label className="block mt-6" data-id="element-4642" data-mp="lg35mselese8">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-4643" data-mp="gg8gouedocvt">
                  Mobile number
                </span>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 50 000 0000" inputMode="tel" className="w-full mt-2 px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm text-dark-navy placeholder:text-slate-300 outline-none focus:border-[var(--mp-50)] focus:ring-4 focus:ring-[var(--mp-10)] transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-4644" data-mp="2s4veftgrfcnt" />
              </label>

              {needsConsent && <ConsentCheckbox checked={agreed} onChange={setAgreed} docs={['terms', 'privacy']} lead="I agree to Talab’s" tone="merchant" className="mt-4" data-id="element-4645" data-mp="2ibxtzhs7jdmk" />}
              {phoneValid && returning && <p className="text-[12px] text-slate-500 mt-3" data-id="element-4646" data-mp="nzszvypyix94">
                  Welcome back — this number already has a Talab account.
                </p>}

              <button disabled={!phoneValid || needsConsent && !agreed} onClick={() => setStage('code')} className="w-full mt-5 py-3 rounded-2xl text-sm font-bold transition-colors duration-150 ease-out disabled:opacity-40" style={{
            background: 'var(--mp)',
            color: 'var(--mp-t)'
          }} data-id="element-4647" data-mp="j2kfdrhc6h4p">
                Continue
              </button>

              <p className="text-[12px] text-slate-400 mt-4 leading-relaxed" data-id="element-4648" data-mp="1gngfezcdexow">
                New here? Continuing creates a Talab account you can use
                across Talab-powered shops.
              </p>
            </> : <>
              <button onClick={() => setStage('phone')} className="inline-flex items-center gap-1.5 mt-5 text-[13px] font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-4649" data-mp="1kq3enlbdfkw4">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-4650" data-mp="29ag6y7jo4vi1" />
                {phone}
              </button>
              <h2 className="font-display font-bold text-xl text-dark-navy mt-3" data-id="element-4651" data-mp="1vanikivm6rd8">
                Enter your code
              </h2>
              <p className="text-[13px] text-slate-500 mt-1.5" data-id="element-4652" data-mp="1zemxwy55rk50">
                We sent a six-digit code to your phone.
              </p>

              <input value={code} onChange={e => setCode(e.target.value.slice(0, 6))} placeholder="••••••" inputMode="numeric" className="w-full mt-5 px-4 py-3 bg-white border border-slate-200 rounded-2xl text-center text-lg font-bold tracking-[0.4em] text-dark-navy placeholder:text-slate-200 outline-none focus:border-[var(--mp-50)] focus:ring-4 focus:ring-[var(--mp-10)] transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-4653" data-mp="1qcdl21mf3eog" />

              <button disabled={code.length < 4} onClick={() => onSignedIn({
            name: returning ? 'Ahmed Hassan' : 'New customer',
            phone: returning ? '+971 50 441 9022' : phone
          })} className="w-full mt-5 py-3 rounded-2xl text-sm font-bold transition-colors duration-150 ease-out disabled:opacity-40" style={{
            background: 'var(--mp)',
            color: 'var(--mp-t)'
          }} data-id="element-4654" data-mp="2sjp8ob5r7bh5">
                Sign in
              </button>
            </>}

          <div className="flex items-center gap-2 mt-6 pt-5 border-t border-slate-100" data-id="element-4655" data-mp="37hrbuk8vhuxv">
            <span className="text-[11px] font-semibold text-slate-400" data-id="element-4656" data-mp="3v8586myhqfy9">
              Powered by
            </span>
            <Logo size="sm" data-id="element-4657" data-mp="3tfy8z04ku4f" />
          </div>
        </div>
      </motion.div>
    </div>;
}

/* ---------------------------------------------------------------- my orders */

type OrderTab = 'All' | 'Current' | 'Completed' | 'Cancelled';

/** An order the shopper can still call off. */
const isActive = (o: Order) => o.status === 'new' || o.status === 'progress';

/** Customer-side reasons — not the merchant's operational list. */
const SHOPPER_CANCEL_REASONS = ['Changed my mind', 'Ordered by mistake', 'Need a different date', 'Found a better option', 'Other'];

/**
 * The shopper's orders WITH THIS MERCHANT — never combined with orders from any
 * other Talab shop. Plain customer wording throughout; no operational terms.
 */
export function ShopMyOrders({
  config,
  orders,
  onBack,
  onMessage
}: {
  config: ShopConfig;
  orders: Order[];
  onBack: () => void;
  onMessage: (text: string) => void;
}) {
  const [tab, setTab] = useState<OrderTab>('All');
  const [openId, setOpenId] = useState<string | null>(null);
  /* A new customer genuinely has no orders, so that is the default. The sample
     set is loaded on demand rather than pretended into existence. */
  const [loaded, setLoaded] = useState(false);
  const [records, setRecords] = useState<Order[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const source = loaded ? records : [];
  const open = source.find(o => o.id === openId) ?? null;
  const loadSamples = () => {
    setRecords(orders);
    setLoaded(true);
  };
  const clearSamples = () => {
    setRecords([]);
    setLoaded(false);
    setOpenId(null);
  };

  /** Cancelling is the shopper's own action, so it is recorded as theirs. */
  const confirmCancel = (order: Order) => {
    setRecords(prev => prev.map(o => o.id === order.id ? {
      ...o,
      status: 'cancelled',
      payment: o.payment === 'paid' ? 'refunded' : o.payment,
      cancelReason: reason === 'Other' && note.trim() ? note.trim() : reason,
      timeline: [...o.timeline, {
        label: 'Cancelled by you',
        time: 'Just now'
      }, ...(o.payment === 'paid' ? [{
        label: 'Refund requested',
        time: 'Just now'
      }] : [])]
    } : o));
    setCancelling(false);
    setReason('');
    setNote('');
  };
  const list = source.filter(o => {
    if (tab === 'Current') return o.status === 'new' || o.status === 'progress';
    if (tab === 'Completed') return o.status === 'fulfilled';
    if (tab === 'Cancelled') return o.status === 'cancelled';
    return true;
  });
  if (open) return <div className="relative z-10 flex-1 overflow-y-auto px-4 lg:px-8 py-8" data-id="element-4658" data-mp="3363zutoqqm5i">
        <div className="mx-auto max-w-2xl" data-id="element-4659" data-mp="3388ea5k9n3k1">
          <button onClick={() => {
        setOpenId(null);
        setCancelling(false);
      }} className="inline-flex items-center gap-2 text-[13px] font-semibold text-[var(--mp-deep)] hover:opacity-70 transition-opacity duration-150 ease-out" data-id="element-4660" data-mp="3rwi32l52kxnq">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-4661" data-mp="3n59utsfh7ic5" />
            All orders
          </button>

          <div className="mt-5 bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4662" data-mp="395qx7tkajpe3">
            <div className="px-7 py-6 border-b border-slate-100" data-id="element-4663" data-mp="3e5n3jzggnswt">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-4664" data-mp="3e30nfbfi852z">
                {config.publicName}
              </p>
              <h1 className="font-display font-bold text-xl text-dark-navy mt-1" data-id="element-4665" data-mp="3488lxwiswy2x">
                Order {open.id}
              </h1>
              <p className="text-[13px] text-slate-500 mt-1" data-id="element-4666" data-mp="yc4laom9k69e">{open.placed}</p>
              <div className="flex flex-wrap items-center gap-2 mt-4" data-id="element-4667" data-mp="12ej1f0yjsrwz">
                <span className="px-3 py-1.5 rounded-full text-[12px] font-bold" style={{
              background: 'var(--mp-10)',
              color: 'var(--mp-deep)'
            }} data-id="element-4668" data-mp="2mx3eutc4fba6">
                  {CUSTOMER_STATUS[open.status]}
                </span>
                <span className="px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-[12px] font-semibold text-slate-600" data-id="element-4669" data-mp="2rvep356bvnsx">
                  {PAYMENT_METHOD_LABEL[methodOf(open)]}
                  {methodOf(open) === 'cash' && ` · ${PAYMENT_LABEL[open.payment]}`}
                </span>
              </div>
            </div>

            <div className="px-7 py-6 space-y-4" data-id="element-4670" data-mp="3amxqx8om8vtd">
              {open.lines.map((l, i) => <div key={i} className="flex items-center gap-3.5" data-id="element-4671" data-mp="1l89h9f7etrz1" data-mpi={i}>
                  <img src={l.image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0 bg-slate-100" data-id="element-4672" data-mp="1lalvpidylk7c" />
                  <div className="min-w-0 flex-1" data-id="element-4673" data-mp="3gdscciua14y5">
                    <p className="text-sm font-bold text-dark-navy truncate" data-id="element-4674" data-mp="3lbtrwtshum4v">
                      {l.name}
                    </p>
                    {l.variant && <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-4675" data-mp="3ub743u805ota">
                        {l.variant}
                      </p>}
                  </div>
                  <p className="text-[13px] text-slate-600 flex-shrink-0" data-id="element-4676" data-mp="33bdkmvjbvay">
                    {l.qty} × {money(l.price)}
                  </p>
                </div>)}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-4677" data-mp="cvqurhz0cfus">
                <span className="text-sm font-semibold text-slate-500" data-id="element-4678" data-mp="ht8uchr4xiq4">
                  Total
                </span>
                <span className="font-display font-bold text-lg text-dark-navy" data-id="element-4679" data-mp="ltsqpj3vwib7">
                  {money(open.total)}
                </span>
              </div>
            </div>

            {open.details.length > 0 && <div className="px-7 py-6 border-t border-slate-100 space-y-2" data-id="element-4680" data-mp="26dxvks8h64i1">
                {open.details.map(d => <div key={d.label} className="flex items-center justify-between gap-4" data-id="element-4681" data-mp="22d481zyy2rpo">
                    <span className="text-[13px] text-slate-500" data-id="element-4682" data-mp="1xfcgx864j9mj">
                      {d.label}
                    </span>
                    <span className="text-[13px] font-semibold text-dark-navy text-right" data-id="element-4683" data-mp="2q1x55zraz5lv">
                      {d.value}
                    </span>
                  </div>)}
                {open.instructions && <p className="text-[13px] text-slate-500 pt-2 leading-relaxed" data-id="element-4684" data-mp="2gb2kgztpge2j">
                    Your note: {open.instructions}
                  </p>}
              </div>}

            {open.status === 'cancelled' && <div className="px-7 py-5 border-t border-slate-100" data-id="element-4685" data-mp="2hcyeogydaq50">
                <p className="text-[13px] font-bold text-dark-navy" data-id="element-4686" data-mp="2hbnbai8u3kef">
                  This order was cancelled
                </p>
                {open.cancelReason && <p className="text-[13px] text-slate-500 mt-1" data-id="element-4687" data-mp="27g3g5y23gpqq">
                    Reason given: {open.cancelReason}
                  </p>}
                {open.payment === 'refunded' && <p className="text-[13px] text-slate-500 mt-1" data-id="element-4688" data-mp="303zbozfxgblx">
                    Your refund has been issued and takes 3–5 working days to
                    appear.
                  </p>}
              </div>}

            <div className="px-7 py-5 border-t border-slate-100" data-id="element-4689" data-mp="2v4d0evjnt5fr">
              {cancelling ? <div data-id="element-4690" data-mp="1fnmbhfl3tg9a">
                  <p className="text-sm font-bold text-dark-navy" data-id="element-4691" data-mp="1fncsibyh89d3">
                    Cancel order {open.id}?
                  </p>
                  <p className="text-[13px] text-slate-500 mt-1 leading-relaxed" data-id="element-4692" data-mp="1jqr9b4u8otrl">
                    {open.status === 'progress' ? `${config.publicName} has already started preparing this order, so they will confirm the cancellation with you.` : `${config.publicName} will be notified straight away.`}
                    {open.payment === 'paid' && ' Anything you have paid will be refunded.'}
                  </p>

                  <div className="mt-4 space-y-2" data-id="element-4693" data-mp="tfyxp6zj9l1k">
                    {SHOPPER_CANCEL_REASONS.map(r => <label key={r} className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border cursor-pointer text-[13px] transition-colors duration-150 ease-out ${reason === r ? 'border-[var(--mp)] bg-[var(--mp-04)]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-4694" data-mp="ohnq72wpnmmi">
                        <input type="radio" name="shopper-cancel-reason" checked={reason === r} onChange={() => setReason(r)} className="accent-[var(--mp)]" data-id="element-4695" data-mp="5putaxeith9i" />
                        <span className="font-semibold text-slate-700" data-id="element-4696" data-mp="1wpk5vtyqhicg">
                          {r}
                        </span>
                      </label>)}
                  </div>

                  {reason && <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder={reason === 'Other' ? 'Tell them what happened' : 'Anything else they should know? (optional)'} className="w-full mt-3 px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-[13px] text-dark-navy placeholder:text-slate-300 outline-none focus:border-[var(--mp-50)] transition-colors duration-150 ease-out resize-none" data-id="element-4697" data-mp="26lditegd7dur" />}

                  <div className="flex flex-wrap items-center gap-2.5 mt-4" data-id="element-4698" data-mp="b4ncjct9fuof">
                    <button disabled={!reason || reason === 'Other' && !note.trim()} onClick={() => confirmCancel(open)} className="px-4 py-2.5 rounded-xl bg-error text-white text-[13px] font-bold hover:bg-red-600 transition-colors duration-150 ease-out disabled:opacity-40" data-id="element-4699" data-mp="3tkpqt9x8ezmz">
                      Cancel this order
                    </button>
                    <button onClick={() => {
                setCancelling(false);
                setReason('');
                setNote('');
              }} className="px-4 py-2.5 rounded-xl border border-slate-200 text-[13px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-4700" data-mp="3fjvfaiew0ik3">
                      Keep my order
                    </button>
                  </div>
                </div> : <div className="flex flex-wrap items-center gap-2.5" data-id="element-4701" data-mp="12dywfsnac80t">
                  <button onClick={() => onMessage(`About my order ${open.id}`)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold transition-colors duration-150 ease-out" style={{
              background: 'var(--mp)',
              color: 'var(--mp-t)'
            }} data-id="element-4702" data-mp="xf5i5y06cczl">
                    <MessageSquareIcon className="w-4 h-4" data-id="element-4703" data-mp="1gc6qnssxsv8a" />
                    Message {config.publicName}
                  </button>
                  {isActive(open) && <button onClick={() => setCancelling(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-[13px] font-semibold text-slate-600 hover:border-error/40 hover:text-error transition-colors duration-150 ease-out" data-id="element-4704" data-mp="291dmul4x58qd">
                      <BanIcon className="w-4 h-4" data-id="element-4705" data-mp="2q9mxy5jbagxb" />
                      Cancel order
                    </button>}
                </div>}
            </div>
          </div>
        </div>
      </div>;
  return <div className="relative z-10 flex-1 overflow-y-auto px-4 lg:px-8 py-8" data-id="element-4706" data-mp="34az8u2jm7scc">
      <div className="mx-auto max-w-2xl" data-id="element-4707" data-mp="348wfb8j97h2v">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-[13px] font-semibold text-[var(--mp-deep)] hover:opacity-70 transition-opacity duration-150 ease-out" data-id="element-4708" data-mp="391foltawrq1v">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-4709" data-mp="3j0wsq6aihvvq" />
          Back to {config.publicName}
        </button>

        <h1 className="font-display font-bold text-2xl text-dark-navy mt-5" data-id="element-4710" data-mp="3n2iao61wl0cs">
          My orders
        </h1>
        <p className="text-[15px] text-slate-500 mt-1.5" data-id="element-4711" data-mp="ojedi3mhy4cx">
          Track your orders with {config.publicName}.
        </p>

        {/* Prototype control — a real shopper's list fills from real orders */}
        <button onClick={loaded ? clearSamples : loadSamples} className="mt-3 text-[12px] font-bold text-slate-400 hover:text-[var(--mp-deep)] transition-colors duration-150 ease-out" data-id="element-4712" data-mp="xcrgjye9ymcq">
          {loaded ? 'Simulate: clear orders' : 'Simulate: load sample orders'}
        </button>

        <div className="flex gap-1 mt-6 border-b border-slate-200/70" data-id="element-4713" data-mp="ouxmmvd7eyc">
          {(['All', 'Current', 'Completed', 'Cancelled'] as OrderTab[]).map(t => <button key={t} onClick={() => setTab(t)} className="relative px-3.5 py-2.5 text-[13px] font-semibold transition-colors duration-150 ease-out" style={{
          color: tab === t ? 'var(--mp-deep)' : '#64748b'
        }} data-id="element-4714" data-mp="9mx4hj1s51tj">
                {t}
                {tab === t && <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full" style={{
            background: 'var(--mp)'
          }} data-id="element-4715" data-mp="1xmiocfck7y8v" />}
              </button>)}
        </div>

        {list.length === 0 ? <div className="mt-8 py-14 px-6 text-center bg-white rounded-3xl border border-slate-100" data-id="element-4716" data-mp="2ccgird131k6i">
            <span className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 inline-flex items-center justify-center" data-id="element-4717" data-mp="2hbjbpz3sfzy4">
              <PackageIcon className="w-5 h-5 text-slate-400" data-id="element-4718" data-mp="27jdo11kkk4oq" />
            </span>
            <p className="text-sm font-bold text-dark-navy mt-4" data-id="element-4719" data-mp="27hanhnlsit5r">
              {loaded ? `No ${tab.toLowerCase()} orders` : 'No orders yet'}
            </p>
            <p className="text-[13px] text-slate-500 mt-1.5 max-w-xs mx-auto leading-relaxed" data-id="element-4720" data-mp="300pypybm7dpq">
              {loaded ? 'Nothing in this tab right now — try another one.' : `When you order from ${config.publicName}, you'll be able to follow it here and cancel it if your plans change.`}
            </p>
            {!loaded && <button onClick={onBack} className="mt-5 px-4 py-2.5 rounded-xl text-[13px] font-bold transition-colors duration-150 ease-out" style={{
          background: 'var(--mp)',
          color: 'var(--mp-t)'
        }} data-id="element-4721" data-mp="2vwfat3e0sp4y">
                Browse {config.publicName}
              </button>}
          </div> : <div className="mt-6 space-y-3" data-id="element-4722" data-mp="2c97l49fffxbk">
            {list.map(o => <div key={o.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-4723" data-mp="2872w5nzxbov9">
                <button onClick={() => setOpenId(o.id)} className="w-full text-left p-5 hover:bg-slate-50/60 transition-colors duration-150 ease-out" data-id="element-4724" data-mp="239kv0js5edxt">
                <div className="flex items-start gap-4" data-id="element-4725" data-mp="nqmvz4653gv0">
                  <img src={o.lines[0].image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0 bg-slate-100" data-id="element-4726" data-mp="rr6sq6mswigm" />
                  <div className="min-w-0 flex-1" data-id="element-4727" data-mp="92rabtjbyu">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-4728" data-mp="40jcmy2qemiz">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-4729" data-mp="3ygmme1kwte9">
                        {o.id}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold" style={{
                    background: 'var(--mp-10)',
                    color: 'var(--mp-deep)'
                  }} data-id="element-4730" data-mp="dre6oom7yqez">
                        {CUSTOMER_STATUS[o.status]}
                      </span>
                    </div>
                    <p className="text-sm font-bold text-dark-navy mt-1.5 truncate" data-id="element-4731" data-mp="25ubzxtgm939w">
                      {o.lines[0].name}
                      {o.lines.length > 1 && ` + ${o.lines.length - 1} more`}
                    </p>
                    {o.lines[0].variant && <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-4732" data-mp="2tqg5alf7glfh">
                        {o.lines[0].variant}
                      </p>}
                    <p className="text-[12px] text-slate-400 mt-2" data-id="element-4733" data-mp="2oq23hl4767pw">
                      {PAYMENT_METHOD_LABEL[methodOf(o)]} · {o.when}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0" data-id="element-4734" data-mp="2yihjicvxom85">
                    <p className="text-sm font-bold text-dark-navy" data-id="element-4735" data-mp="3ci0hwdyy0j79">
                      {money(o.total)}
                    </p>
                    <ChevronRightIcon className="w-4 h-4 text-slate-300" data-id="element-4736" data-mp="37gjh0iggkwqo" />
                  </div>
                </div>
                </button>

                {/* Cancelling shouldn't require opening the order first */}
                {isActive(o) && <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 bg-slate-50/50" data-id="element-4737" data-mp="3i2pw3bb5d64w">
                    <p className="text-[12px] text-slate-500" data-id="element-4738" data-mp="3m7635fwhyjlp">
                      {o.status === 'new' ? 'You can still cancel this order.' : `${config.publicName} has started this order.`}
                    </p>
                    <button onClick={() => {
              setOpenId(o.id);
              setCancelling(true);
            }} className="inline-flex items-center gap-1.5 text-[12px] font-bold text-slate-500 hover:text-error transition-colors duration-150 ease-out flex-shrink-0" data-id="element-4739" data-mp="2ygjtjk6ugr9l">
                      <BanIcon className="w-3.5 h-3.5" data-id="element-4740" data-mp="1hcdxarxgkibw" />
                      Cancel
                    </button>
                  </div>}
              </div>)}
          </div>}
      </div>
    </div>;
}